import type { ResolvedColorTheme } from '@/core/theme';

const SOURCE = 'mantis-embed';
const VERSION = 1;

export type EmbedTokenExpiredReason = 'expiring' | 'unauthorized';

type EmbedEnvelope = { source: typeof SOURCE; v: typeof VERSION };

export type EmbedReadyMessage = EmbedEnvelope & { type: 'ready' };
export type EmbedTokenMessage = EmbedEnvelope & { type: 'token'; accessToken: string; expiresAt: number };
export type EmbedTokenExpiredMessage = EmbedEnvelope & { type: 'token-expired'; reason: EmbedTokenExpiredReason };
export type EmbedThemeMessage = EmbedEnvelope & { type: 'theme'; theme: ResolvedColorTheme };

export type EmbedMessage = EmbedReadyMessage | EmbedTokenMessage | EmbedTokenExpiredMessage | EmbedThemeMessage;
export type EmbedMessageType = EmbedMessage['type'];

const EMBED_MESSAGE_TYPES: readonly string[] = ['ready', 'token', 'token-expired', 'theme'] satisfies EmbedMessageType[];

export class EmbedProtocol {
  static readonly SOURCE = SOURCE;
  static readonly VERSION = VERSION;

  /** Envelope check only (source, version, known type); payload fields are not validated. */
  static isEmbedMessage(data: unknown, type: 'ready'): data is EmbedReadyMessage;
  static isEmbedMessage(data: unknown, type: 'token'): data is EmbedTokenMessage;
  static isEmbedMessage(data: unknown, type: 'token-expired'): data is EmbedTokenExpiredMessage;
  static isEmbedMessage(data: unknown, type: 'theme'): data is EmbedThemeMessage;
  static isEmbedMessage(data: unknown, type?: EmbedMessageType): data is EmbedMessage;
  static isEmbedMessage(data: unknown, type?: EmbedMessageType): boolean {
    if (typeof data !== 'object' || data === null) return false;

    const message = data as Record<string, unknown>;

    if (message.source !== EmbedProtocol.SOURCE || message.v !== EmbedProtocol.VERSION) return false;
    if (typeof message.type !== 'string' || !EMBED_MESSAGE_TYPES.includes(message.type)) return false;

    return type === undefined || message.type === type;
  }

  static buildTokenMessage({ accessToken, expiresAt }: { accessToken: string; expiresAt: number }): EmbedTokenMessage {
    return {
      source: EmbedProtocol.SOURCE,
      v: EmbedProtocol.VERSION,
      type: 'token',
      accessToken,
      expiresAt,
    };
  }

  static buildThemeMessage(theme: ResolvedColorTheme): EmbedThemeMessage {
    return {
      source: EmbedProtocol.SOURCE,
      v: EmbedProtocol.VERSION,
      type: 'theme',
      theme,
    };
  }
}
